import {
  Button,
  Checkbox,
  Combobox,
  ComboboxOption,
  Loader,
  Modal,
  ModalContent,
  ModalFooter,
  TextField,
  Toast
} from '@deque/cauldron-react';
import React, { useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import * as uuid from 'uuid';
import type { AdminFeatureFlag } from '~/common/api-client';
import type { OrNull } from '~/typings/utils';
import styles from './AddEnterpriseFeatureFlagOverrideModal.css';

export interface AddEnterpriseFeatureFlagOverrideModalProps {
  show: boolean;
  features: AdminFeatureFlag[];
  onClose: () => void;
  onSave: (
    featureId: string,
    enterpriseIds: string[],
    state: boolean
  ) => Promise<void>;
}

const AddEnterpriseFeatureFlagOverrideModal = ({
  show,
  features,
  onClose,
  onSave
}: AddEnterpriseFeatureFlagOverrideModalProps): JSX.Element => {
  const { t } = useTranslation();

  const [selectedFeatureId, setSelectedFeatureId] = useState('');
  const [enterpriseId, setEnterpriseId] = useState('');
  const [stateValue, setStateValue] = useState(false);
  const [saving, setSaving] = useState(false);
  const [overrideError, setOverrideError] = useState<OrNull<string>>(null);
  const [enterpriseIdError, setEnterpriseIdError] =
    useState<OrNull<string>>(null);

  const loaderRef = useCallback(
    (element: OrNull<HTMLDivElement>) => {
      if (saving && element) {
        element.focus();
      }
    },
    [saving]
  );

  const handleFeatureSelect = (featureId: string) => {
    const feature = features.find(f => f.id === featureId);
    setSelectedFeatureId(featureId);
    if (feature) {
      setStateValue(!feature.default_state);
    }
  };

  const handleClose = () => {
    setSelectedFeatureId('');
    setEnterpriseId('');
    setStateValue(false);
    setOverrideError(null);
    setEnterpriseIdError(null);
    onClose();
  };

  const isFormValid = (): boolean => {
    if (!uuid.validate(enterpriseId.trim())) {
      setEnterpriseIdError(t('Enterprise ID must be a valid UUID'));
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!isFormValid()) {
      return;
    }

    setSaving(true);
    setOverrideError(null);
    setEnterpriseIdError(null);

    try {
      await onSave(selectedFeatureId, [enterpriseId.trim()], stateValue);
      handleClose();
    } catch (err) {
      setOverrideError(
        `${t('Failed to add override')}. ${(err as Error).message}`
      );
    } finally {
      setSaving(false);
    }
  };

  const canSave =
    selectedFeatureId.length > 0 && enterpriseId.trim().length > 0;

  const selectedFeature =
    features.find(f => f.id === selectedFeatureId) || null;

  const defaultFeatureStateLabel = selectedFeature?.default_state
    ? t('Enabled')
    : t('Disabled');

  return (
    <Modal
      className={styles.modal}
      heading={t('Add Override')}
      show={show}
      onClose={handleClose}
    >
      {overrideError && (
        <Toast
          className={styles.overrideErrorToast}
          type="error"
          onDismiss={() => setOverrideError('')}
          show
        >
          {overrideError}
        </Toast>
      )}
      <ModalContent className={styles.modalContent}>
        {saving && (
          <Loader
            label={t('Adding enterprise feature flag override')}
            tabIndex={-1}
            ref={loaderRef}
          />
        )}
        {/* Combobox keeps the state internally,
          so we don't unmount it and keep it hidden*/}
        <div hidden={saving}>
          <Combobox
            label={t('Feature flag')}
            placeholder={t('Select feature')}
            required
            className={styles.featuresCombobox}
            name="feature-flag"
            onSelectionChange={({ value }) => {
              handleFeatureSelect(String(value));
            }}
          >
            {features.map(feature => (
              <ComboboxOption key={feature.id} value={feature.id}>
                {feature.id}
              </ComboboxOption>
            ))}
          </Combobox>

          <TextField
            required
            error={enterpriseIdError}
            label={t('Enterprise ID')}
            placeholder={t('Enter enterprise ID')}
            value={enterpriseId}
            onChange={value => setEnterpriseId(value)}
          />

          {selectedFeatureId && (
            <Checkbox
              name="override-state"
              labelDescription={
                <p className={styles.formItemDescription}>
                  {t('Current global state: ')}
                  <strong>{defaultFeatureStateLabel}</strong>
                </p>
              }
              label={t('Override state')}
              id="override-state"
              onChange={e => setStateValue(e.target.checked)}
              checked={stateValue}
            />
          )}
        </div>
      </ModalContent>
      <ModalFooter>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!canSave || saving}
        >
          {t('Save')}
        </Button>
        <Button variant="secondary" onClick={handleClose} disabled={saving}>
          {t('Cancel')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddEnterpriseFeatureFlagOverrideModal;
